import React, { useState } from 'react';
import SelectField from './SelectField';
import TextInput from './TextInput';
import PhotoUploadGrid from './PhotoUploadGrid';
import { FormData } from '../types';
import '../styles/Section.css';

interface Props {
  formData: FormData;
  onChange: (field: keyof FormData, value: string) => void;
}

const KeyPhotoSection: React.FC<Props> = ({ formData, onChange }) => {
  const [result, setResult] = useState<string>('');
  const [showResult, setShowResult] = useState<boolean>(false);
  const [isMainPhotoExpanded, setIsMainPhotoExpanded] = useState<boolean>(false);
  const [isFrame1Expanded, setIsFrame1Expanded] = useState<boolean>(false);
  const [isFrame2Expanded, setIsFrame2Expanded] = useState<boolean>(false);

  const generatePrompt = () => {
    let prompt = '';

    // Get form values
    const lightingStyle = formData.lightingStyle || 'Natural Light';
    const background = formData.backgroundSetting || 'Neutral';
    const jewelryTerm = formData.jewelryItem || 'jewelry';
    const materialTerm = formData.mainMaterial || 'precious metals';
    const styleTerm = formData.styleAesthetic || 'elegant';
    const shotType = formData.photoType || 'Product Only';
    const eventType = formData.eventType || '';

    // Random variations for creative diversity
    const modelPoses = [
      'looking directly at camera with confident expression',
      'glancing to the side with elegant pose',
      'looking slightly down with serene expression',
      'tilted head with gentle smile',
      'looking over shoulder gracefully',
      'natural relaxed pose with soft gaze',
      'profile view with poised stance',
      'three-quarter view with sophisticated demeanor',
      'hand gently touching jewelry piece',
      'dynamic pose with natural movement'
    ];

    const cameraAngles = [
      'eye-level straight-on angle',
      'slightly elevated angle looking down',
      'low angle looking up slightly',
      'three-quarter angle from the side',
      'dutch angle for dynamic composition',
      'frontal centered composition',
      'off-center rule of thirds composition',
      'close framing emphasizing subject'
    ];

    const headDirections = [
      'facing forward',
      'turned slightly left',
      'turned slightly right',
      'angled toward light source',
      'in natural relaxed position'
    ];

    const photographers = [
      'Kate Benson',
      'Lisa Saad',
      'Cass Bird',
      'Tsour Lee Adato',
      'David Newton',
      'Julia Hetta'
    ];

    // Add photographer style reference
    const randomPhotographer = photographers[Math.floor(Math.random() * photographers.length)];

    // Add camera angle variety
    const randomAngle = cameraAngles[Math.floor(Math.random() * cameraAngles.length)];

    // Generate main photo prompt based on shot type
    if (shotType === 'On Model') {
      // Determine model description based on selection
      const modelType = formData.modelType || 'Diverse';
      let modelDescription = '';
      if (modelType === 'Diverse') {
        modelDescription = 'a model of diverse ethnicity';
      } else if (modelType === 'White') {
        modelDescription = 'a white model';
      } else if (modelType === 'Asian') {
        modelDescription = 'an Asian model';
      } else if (modelType === 'Latina') {
        modelDescription = 'a Latina model';
      } else if (modelType === 'Black') {
        modelDescription = 'a Black model';
      } else {
        modelDescription = 'a model of diverse ethnicity';
      }

      prompt = `A ${randomAngle} close-up ${lightingStyle} photo in the style of ${randomPhotographer}, featuring ${modelDescription} wearing ${jewelryTerm}. The model is elegantly posed to highlight the jewelry against a ${background} backdrop. CRITICAL SKIN QUALITY: Model has perfectly smooth, flawless, natural-looking skin with beautiful even tone - absolutely zero noise, zero grain, zero distortion, zero artifacts, zero texture issues. Skin should appear clean, healthy, and naturally beautiful without any digital noise or imperfections. Focus on texture and luxurious detail of the jewelry.`;
    } else {
      // Product shot
      prompt = `A ${randomAngle} professional ${lightingStyle} photograph in the style of ${randomPhotographer}, of ${jewelryTerm}. The jewelry is displayed against a ${background} background, emphasizing the piece's elegance and craftsmanship. Product-only shot with no model.`;
    }

    // Add event styling if specified
    if (eventType && eventType !== 'None') {
      prompt += ` Styled for ${eventType} with appropriate atmosphere and elegant details.`;
    }

    if (formData.keyPhotoOtherDetails.trim() !== '') {
      prompt += ` ${formData.keyPhotoOtherDetails.trim()}.`;
    }

    prompt += ` IMPORTANT: Absolutely NO text, NO logos, NO writing, NO letters, NO words, NO brand names anywhere on the image. Clean product photography only. Professional jewelry photography, high resolution, commercial quality, studio lighting, detailed texture, luxury aesthetic, editorial style. For ${formData.platform}, aspect ratio: ${formData.aspectRatio}.`;

    setResult(prompt);
    setShowResult(true);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(result);
      alert('Prompt copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="section">
      <PhotoUploadGrid formData={formData} onChange={onChange} />

      {/* Step 1: Platform & Format */}
      <div className="section-content">
        <div className="step-header">
          <div className="step-number">1</div>
          <h3 className="step-title">Platform & Format</h3>
        </div>

        <SelectField
          label="Platform"
          value={formData.platform}
          onChange={(value) => onChange('platform', value)}
          options={['Etsy Store', 'Instagram Reels', 'TikTok', 'YouTube']}
        />
        <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '-12px', marginBottom: '16px' }}>
          Defines where the content will be used
        </p>

        <SelectField
          label="Aspect Ratio"
          value={formData.aspectRatio}
          onChange={(value) => onChange('aspectRatio', value)}
          options={['1:1', '16:9', '9:16', '4:5']}
        />
        <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '-12px', marginBottom: '16px' }}>
          Optimizes image dimensions for the selected platform
        </p>
      </div>

      {/* Step 2: Describe Your Jewelry */}
      <div className="section-content">
        <div className="step-header">
          <div className="step-number">2</div>
          <h3 className="step-title">Describe Your Jewelry</h3>
        </div>

        <SelectField
          label="Jewelry Item"
          value={formData.jewelryItem}
          onChange={(value) => onChange('jewelryItem', value)}
          options={[
            'Necklace',
            'Earring',
            'Ring',
            'Bracelet',
            'Pendant',
            'Bangle'
          ]}
          allowCustom={true}
        />
        <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '-12px', marginBottom: '16px' }}>
          Select the type of jewelry
        </p>
      </div>

      {/* Photo Settings Grid (2 cards per row) */}
      <div className="photo-settings-grid">
        {/* Step 3: Main Photo (Collapsible) */}
        <div className="section-content collapsible-section">
          <div 
            className="step-header collapsible-header" 
            onClick={() => setIsMainPhotoExpanded(!isMainPhotoExpanded)}
            style={{ cursor: 'pointer' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
              <div className="step-number">3</div>
              <h3 className="step-title">Main Photo</h3>
            </div>
            <span style={{ fontSize: '16px', color: '#666' }}>
              {isMainPhotoExpanded ? '▼' : '▶'}
            </span>
          </div>

          {isMainPhotoExpanded && (
            <div style={{ marginTop: '16px' }}>
              <SelectField
                label="Shot Type"
                value={formData.photoType}
                onChange={(value) => onChange('photoType', value)}
                options={['Product Only', 'On Model']}
              />

              <SelectField
                label="Background"
                value={formData.backgroundSetting}
                onChange={(value) => onChange('backgroundSetting', value)}
                options={['White', 'Neutral', 'Lifestyle', 'Natural']}
                allowCustom={true}
              />

              <SelectField
                label="Lighting Style"
                value={formData.lightingStyle}
                onChange={(value) => onChange('lightingStyle', value)}
                options={['Natural Light', 'Studio Light', 'Dramatic']}
                allowCustom={true}
              />

              <SelectField
                label="Models"
                value={formData.modelType || 'Diverse'}
                onChange={(value) => onChange('modelType', value)}
                options={['Diverse', 'White', 'Asian', 'Latina', 'Black']}
                allowCustom={true}
              />
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '-12px', marginBottom: '16px' }}>
                Choose the model appearance
              </p>

              <SelectField
                label="Special Event"
                value={formData.eventType || 'None'}
                onChange={(value) => onChange('eventType', value)}
                options={['None', 'Engagement', 'Wedding', 'Christmas', "Valentine's Day"]}
                allowCustom={true}
              />
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '-12px', marginBottom: '16px' }}>
                Optional thematic context
              </p>

              <TextInput
                label="Other Details"
                value={formData.keyPhotoOtherDetails}
                onChange={(value) => onChange('keyPhotoOtherDetails', value)}
                placeholder="Special effects, styling instructions, or specific visual requirements..."
                multiline
              />
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '-12px', marginBottom: '16px' }}>
                Free text field (up to 250 characters)
              </p>
            </div>
          )}
        </div>

        {/* Step 4: Second Photo (Collapsible) */}
        <div className="section-content collapsible-section">
          <div 
            className="step-header collapsible-header" 
            onClick={() => setIsFrame1Expanded(!isFrame1Expanded)}
            style={{ cursor: 'pointer' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
              <div className="step-number">4</div>
              <h3 className="step-title">Second Photo</h3>
            </div>
            <span style={{ fontSize: '16px', color: '#666' }}>
              {isFrame1Expanded ? '▼' : '▶'}
            </span>
          </div>

          {isFrame1Expanded && (
            <div style={{ marginTop: '16px' }}>
              <SelectField
                label="Shot Type"
                value={formData.frame1ShotType}
                onChange={(value) => onChange('frame1ShotType', value)}
                options={['Product-Only', 'On-Model']}
              />

              <SelectField
                label="Action / Composition"
                value={formData.frame1Action}
                onChange={(value) => onChange('frame1Action', value)}
                options={['Studio Shot', 'Close-Up', 'Lifestyle', 'Gift View', 'Scale Shot', 'Detail', 'Creative Angle']}
              />

              <TextInput
                label="Other Details"
                value={formData.frame1OtherDetails}
                onChange={(value) => onChange('frame1OtherDetails', value)}
                placeholder="Custom instructions (up to 250 characters)"
                multiline
              />
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '-12px', marginBottom: '16px' }}>
                Free text field for custom instructions
              </p>
            </div>
          )}
        </div>

        {/* Step 5: Third Photo (Collapsible) */}
        <div className="section-content collapsible-section">
          <div 
            className="step-header collapsible-header" 
            onClick={() => setIsFrame2Expanded(!isFrame2Expanded)}
            style={{ cursor: 'pointer' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
              <div className="step-number">5</div>
              <h3 className="step-title">Third Photo</h3>
            </div>
            <span style={{ fontSize: '16px', color: '#666' }}>
              {isFrame2Expanded ? '▼' : '▶'}
            </span>
          </div>

          {isFrame2Expanded && (
            <div style={{ marginTop: '16px' }}>
              <SelectField
                label="Shot Type"
                value={formData.frame2ShotType}
                onChange={(value) => onChange('frame2ShotType', value)}
                options={['Product-Only', 'On-Model']}
              />

              <SelectField
                label="Angle & Zoom"
                value={formData.frame2AngleZoom}
                onChange={(value) => onChange('frame2AngleZoom', value)}
                options={['Macro', 'Front View', 'Top View', '45° Angle', 'Side Profile', 'Eye Level', 'Body Focus']}
              />

              <TextInput
                label="Other Details"
                value={formData.frame2OtherDetails}
                onChange={(value) => onChange('frame2OtherDetails', value)}
                placeholder="Scene or styling notes: environment, mood, props..."
                multiline
              />
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '-12px', marginBottom: '16px' }}>
                Free text field for scene or styling notes (up to 250 characters)
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Generate Button */}
      <div className="section-content">
        <button className="generate-button" onClick={generatePrompt}>
          Generate Image
        </button>

        {showResult && (
          <div className="result-container">
            <div className="result-header">
              <h3>Generated Prompt</h3>
              <button className="copy-button" onClick={copyToClipboard}>
                📋 Copy
              </button>
            </div>
            <div className="result-text">{result}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KeyPhotoSection;


